import { Link, useParams } from 'react-router-dom';
import { ClaimRow } from '../components/ClaimRow';
import { EventRow } from '../components/EventRow';
import { JobCard } from '../components/JobCard';
import { Pill } from '../components/Pill';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { TruncationNotice } from '../components/TruncationNotice';
import { useCompany } from '../hooks/useRadarQuery';
import { compactMoney, label, readiness } from '../lib/format';

export function CompanyPage() {
  const { slug = '' } = useParams();
  const query = useCompany(slug);
  if (query.isLoading) return <LoadingPanel label="Loading company evidence…" />;
  if (query.isError || !query.data) return <ErrorPanel error={query.error} />;
  const company = query.data;
  const location = [company.headquarters.city, company.headquarters.state, company.headquarters.country].filter(Boolean).join(', ');
  return (
    <>
      <Link className="back-link" to="/companies">← Company registry</Link>
      <header className="company-hero">
        <div>
          <div className="pill-row"><Pill value={company.reviewStatus} tone={company.reviewStatus === 'verified' ? 'positive' : 'warning'} /><Pill value={`evidence ${company.evidenceGrade} · ${company.sources?.total ?? 0} sources`} /><Pill value={company.cohort} tone="accent" /></div>
          <h1>{company.name}</h1>
          <p>{company.description}</p>
          <div className="company-hero__links">{company.websiteUrl && <a href={company.websiteUrl} target="_blank" rel="noreferrer">Company website ↗</a>}<span>{location || 'Headquarters unverified'}</span></div>
        </div>
        <div className="feedback-score"><strong>{company.physicalFeedbackLevel}</strong><span>of 5</span><small>physical feedback</small></div>
      </header>
      <section className="detail-layout">
        <div className="panel">
          <div className="panel__heading"><div><span className="eyebrow">Evidence timeline</span><h2>Progress and company signals</h2></div><span>{company.events?.total ?? 0} events</span></div>
          <TruncationNotice shown={company.events?.items.length ?? 0} total={company.events?.total ?? 0} noun="events" />
          {company.events?.items.length ? company.events.items.map((event) => <EventRow key={event.id} event={event} />) : <EmptyPanel message="No evidence-backed events have been recorded." />}
        </div>
        <aside className="panel profile-panel">
          <span className="eyebrow">Current profile</span><h2>Company facts</h2>
          <dl>
            <div><dt>Stage</dt><dd>{label(company.fundingStage)}</dd></div>
            <div><dt>Total funding</dt><dd>{compactMoney(company.totalDisclosedFundingUsd)}</dd></div>
            <div><dt>Latest round</dt><dd>{compactMoney(company.latestRoundAmountUsd)}</dd></div>
            <div><dt>Founded</dt><dd>{company.foundedYear ?? 'Unknown'}</dd></div>
            <div><dt>Technology readiness</dt><dd>{readiness(company.technologyReadinessLevel, 9)}</dd></div>
            <div><dt>Commercial readiness</dt><dd>{readiness(company.commercialReadinessLevel, 6)}</dd></div>
          </dl>
          <div className="tag-group"><span>Domains</span><div className="pill-row">{company.domains.map((item) => <Pill key={item} value={item} />)}</div></div>
          <div className="tag-group"><span>Modeling tasks</span><div className="pill-row">{company.tasks.map((item) => <Pill key={item} value={item} />)}</div></div>
          <div className="tag-group"><span>Paradigms</span><div className="pill-row">{company.paradigms.map((item) => <Pill key={item} value={item} />)}</div></div>
        </aside>
      </section>
      <section className="panel section-gap">
        <div className="panel__heading"><div><span className="eyebrow">Hiring</span><h2>Current and historical roles</h2></div><span>{company.jobs?.total ?? 0} observed</span></div>
        <TruncationNotice shown={company.jobs?.items.length ?? 0} total={company.jobs?.total ?? 0} noun="observed roles" />
        <div className="card-grid">{company.jobs?.items.length ? company.jobs.items.map((job) => <JobCard key={job.id} job={job} />) : <EmptyPanel message="No roles have been observed for this company." />}</div>
      </section>
      <section className="panel section-gap">
        {/* Served, capped and graded since assertions were derived, and shown
            nowhere. This is the view the derivation was for: competing claims
            about one predicate are told apart by how well each is attested. */}
        <div className="panel__heading"><div><span className="eyebrow">Claims</span><h2>Sourced facts and their support</h2></div><span>{company.assertions?.total ?? 0} recorded</span></div>
        <TruncationNotice shown={company.assertions?.items.length ?? 0} total={company.assertions?.total ?? 0} noun="claims" />
        {company.assertions?.items.length
          ? company.assertions.items.map((claim) => <ClaimRow key={claim.id} claim={claim} />)
          : <EmptyPanel message="No sourced claims have been recorded." />}
      </section>
      <section className="panel section-gap">
        <div className="panel__heading"><div><span className="eyebrow">Provenance</span><h2>Primary and supporting sources</h2></div><span>{company.sources?.total ?? 0} sources</span></div>
        <div className="source-list">{company.sources?.items.length ? company.sources.items.map((source) => <a key={source.id} href={source.url} target="_blank" rel="noreferrer"><span><strong>{source.title}</strong><small>{label(source.sourceType)} · Tier {source.tier}{source.isPrimary ? ' · Primary' : ''}</small></span><b>↗</b></a>) : <EmptyPanel message="No sources attached." />}</div>
      </section>
    </>
  );
}
